import { getAuth, sendEmailVerification, signOut } from "firebase/auth";
import app from "../../../firebase";

const PROFILE_ROUTE = "/profile";

const handleAuthAction = (currentUser, logOut, setShowLoginChoiceDialog) => {
  if (currentUser) {
    logOut();
    window.location.href = "/";
  } else {
    setShowLoginChoiceDialog(true);
  }
};

const auth = getAuth(app);

const handleGoogleLogin = async (loginWithGoogle, setShowLoginChoiceDialog) => {
  const user = await loginWithGoogle();
  setShowLoginChoiceDialog(false);
  if (user) {
    window.location.href = PROFILE_ROUTE;
  }
};

const handleEmailPasswordLogin = async (
  email,
  password,
  loginWithEmailPassword,
  setNotificationMessage,
  setIsError,
  setShowNotification,
  setShowLoginDialog
) => {
  try {
    const { user } = await loginWithEmailPassword(email, password);

    if (!user.emailVerified) {
      setIsError(true);
      setNotificationMessage(
        "Your email is not verified yet. Please check your inbox (including spam) for the verification link."
      );
      setShowNotification(true);
      return;
    }

    // Verified — close the dialog and send the user to their profile.
    setShowLoginDialog(false);
    window.location.href = PROFILE_ROUTE;
  } catch (error) {
    setIsError(true);
    setNotificationMessage(
      error.message === "Firebase: Error (auth/invalid-login-credentials)."
        ? "Invalid email or password. Please try again."
        : error.message
    );
    setShowNotification(true);
  }
};

const handlePasswordReset = async (
  email,
  resetPassword,
  setNotificationMessage,
  setIsError,
  setShowNotification,
  setShowLoginDialog,
  setShowResetPasswordDialog
) => {
  try {
    await resetPassword(email);
    setNotificationMessage(
      "We've sent a password reset link to your email. Please check your inbox (and spam folder) to continue."
    );
    setIsError(false);
    setShowResetPasswordDialog(false);
    // Don't auto-open the login dialog — the user can choose when they're
    // ready by clicking "Log In" again. Showing two dialogs at once looked stacked.
  } catch (error) {
    setNotificationMessage(
      error?.code === "auth/invalid-email"
        ? "Please enter a valid email address."
        : error?.code === "auth/too-many-requests"
          ? "Too many attempts. Please wait a moment and try again."
          : "We couldn't send the reset email. Please check the address and try again."
    );
    setIsError(true);
  }
  setShowNotification(true);
};

const handleEmailPasswordSignUp = async (
  name,
  email,
  password,
  signUpWithEmailPassword,
  setShowSignUpDialog,
  setNotificationMessage,
  setShowNotification,
  setIsError
) => {
  try {
    const userCredential = await signUpWithEmailPassword(email, password, name);

    try {
      const { emailVerified } = userCredential.user;
      await sendEmailVerification(userCredential.user);

      if (!emailVerified) {
        await signOut(auth);
      }

      setNotificationMessage(
        "Account created successfully. Please check your email to verify your account before signing in. (Don't forget to check your spam folder.)"
      );
      setIsError(false);
    } catch (verificationError) {
      console.error("Error sending verification email: ", verificationError);
      setNotificationMessage(
        "Your account was created, but we couldn't send the verification email. Please double-check your email address and try again."
      );
      setIsError(true);
    }
  } catch (error) {
    console.error("Error during email/password sign up: ", error);
    setNotificationMessage(error.message);
    setIsError(true);
  }

  setShowSignUpDialog(false);
  setShowNotification(true);
};

const openEmailLoginDialog = (
  setShowLoginChoiceDialog,
  setShowSignUpDialog,
  setShowLoginDialog,
  setShowResetPasswordDialog
) => {
  setShowLoginChoiceDialog(false);
  setShowSignUpDialog(false);
  setShowResetPasswordDialog(false);
  setShowLoginDialog(true);
};

const openEmailSignUpDialog = (setShowLoginDialog, setShowSignUpDialog) => {
  setShowLoginDialog(false);
  setShowSignUpDialog(true);
};

const openResetPasswordDialog = (setShowResetPasswordDialog, setShowSignUpDialog, setShowLoginDialog) => {
  setShowResetPasswordDialog(true);
  setShowSignUpDialog(false);
  setShowLoginDialog(false);
};

export {
  handleAuthAction,
  handleGoogleLogin,
  handleEmailPasswordLogin,
  handleEmailPasswordSignUp,
  handlePasswordReset,
  openEmailLoginDialog,
  openEmailSignUpDialog,
  openResetPasswordDialog,
};
